import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  Badge,
  Modal,
  Form,
  Alert,
  Spinner,
  InputGroup,
  Pagination,
  Row,
  Col,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaQuestionCircle,
  FaCopy,
} from "react-icons/fa";
import { toast } from "react-toastify";
import TestService from "../../../services/testService";
import QuestionImportModal from "../../../components/admin/tests/QuestionImportModal";

const ManageQuestions = ({ categoryId, categoryName, onRefresh }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1,
  });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    question: "",
    option_a: "",
    option_b: "",
    option_c: "",
    option_d: "",
    correct_option: "a",
    marks: 1,
    negative_marks: 0,
    has_negative_marking: false,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingQuestion, setDeletingQuestion] = useState(null);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    if (categoryId) {
      loadQuestions();
    }
  }, [categoryId, pagination.current_page, itemsPerPage, searchTerm]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const response = await TestService.getQuestions(
        categoryId,
        pagination.current_page,
        itemsPerPage,
        searchTerm,
      );
      console.log("Questions response:", response);
      
      // Handle the complex nested structure carefully
      let questionsArray = [];
      let paginationData = null;
      
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          questionsArray = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          questionsArray = response.data.data;
          paginationData = response.data.pagination;
        }
      }
      
      setQuestions(questionsArray);
      if (paginationData) {
        setPagination(paginationData);
      }
    } catch (error) {
      console.error("Failed to load questions:", error);
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQuestion = async () => {
    const required = [
      "question",
      "option_a",
      "option_b",
      "option_c",
      "option_d",
    ];
    for (let field of required) {
      if (!formData[field]?.trim()) {
        toast.error("Please fill all fields");
        return;
      }
    }

    // Ensure correct data types
    const data = {
      category_id: parseInt(categoryId),
      question: formData.question,
      option_a: formData.option_a,
      option_b: formData.option_b,
      option_c: formData.option_c,
      option_d: formData.option_d,
      correct_option: formData.correct_option,
      marks: parseInt(formData.marks) || 1,
      negative_marks: formData.has_negative_marking
        ? parseInt(formData.negative_marks) || 0
        : 0,
      has_negative_marking: formData.has_negative_marking ? 1 : 0,
    };

    console.log("Sending data to backend:", data);

    try {
      if (editingQuestion) {
        await TestService.updateQuestion(editingQuestion.id, data);
        toast.success("Question updated successfully");
      } else {
        await TestService.createQuestion(data);
        toast.success("Question added successfully");
      }
      closeModal(); // Use closeModal instead of setShowModal(false)
      resetForm();
      loadQuestions();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Failed to save question:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.message || "Failed to save question");
    }
  };

  const handleDeleteQuestion = async () => {
    if (!deletingQuestion) return;

    try {
      await TestService.deleteQuestion(deletingQuestion.id);
      toast.success("Question deleted successfully");
      setShowDeleteModal(false);
      setDeletingQuestion(null);
      loadQuestions();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Failed to delete question:", error);
      toast.error("Failed to delete question");
    }
  };

  const resetForm = () => {
    setEditingQuestion(null);
    setFormData({
      question: "",
      option_a: "",
      option_b: "",
      option_c: "",
      option_d: "",
      correct_option: "a",
      marks: 1,
      negative_marks: 0,
      has_negative_marking: false,
    });
  };

  // NEW: Open add modal with empty form
  const openAddModal = () => {
    resetForm(); // Reset form before opening
    setShowModal(true);
  };

  // NEW: Close modal and reset form
  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const openEditModal = (question) => {
    setEditingQuestion(question);
    setFormData({
      question: question.question,
      option_a: question.option_a,
      option_b: question.option_b,
      option_c: question.option_c,
      option_d: question.option_d,
      correct_option: question.correct_option,
      marks: question.marks,
      negative_marks: question.negative_marks || 0,
      has_negative_marking:
        question.has_negative_marking === 1 ||
        question.has_negative_marking === true,
    });
    setShowModal(true);
  };

  const handleImportSuccess = () => {
    loadQuestions();
    if (onRefresh) onRefresh();
    toast.success("Questions imported successfully");
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.total_pages) {
      setPagination((prev) => ({ ...prev, current_page: pageNumber }));
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const renderPagination = () => {
    const { current_page, total_pages } = pagination;
    if (total_pages <= 1) return null;

    let items = [];
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(current_page - 1)}
        disabled={current_page === 1}
      />,
    );

    for (let i = 1; i <= total_pages; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === current_page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(current_page + 1)}
        disabled={current_page === total_pages}
      />,
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <Form.Select
            size="sm"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={{ width: "auto" }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <Pagination size="sm">{items}</Pagination>
      </div>
    );
  };

  if (!categoryId) {
    return (
      <Alert variant="info" className="text-center py-5">
        <FaQuestionCircle size={40} className="mb-3 text-muted" />
        <h5>Select a Category</h5>
        <p>Please select a category from the left to manage questions.</p>
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          Questions - {categoryName}
          <Badge bg="secondary" className="ms-2">
            Total: {pagination.total}
          </Badge>
        </h5>
        <div>
          <Button
            variant="outline-primary"
            size="sm"
            className="me-2"
            onClick={() => setShowImportModal(true)}
            disabled={!categoryId}
          >
            <FaCopy className="me-1" /> Import Questions
          </Button>
          <Button size="sm" onClick={openAddModal}>
            <FaPlus className="me-1" /> Add Question
          </Button>
        </div>
      </div>

      {/* Search */}
      <InputGroup className="mb-3" style={{ width: "300px" }}>
        <Form.Control
          placeholder="Search questions..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
        />
        <Button variant="outline-primary" onClick={handleSearch}>
          <FaSearch />
        </Button>
      </InputGroup>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : questions.length === 0 ? (
        <Alert variant="info" className="text-center">
          No questions in this category. Click "Add Question" to get started.
        </Alert>
      ) : (
        <>
          <Table striped hover responsive>
            <thead>
              <tr>
                <th style={{ width: "50px" }}>#</th>
                <th>Question</th>
                <th>Options</th>
                <th>Correct</th>
                <th>Marks</th>
                <th>Negative</th>
                <th style={{ width: "100px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {questions.map((q, idx) => (
                <tr key={q.id}>
                  <td>
                    {(pagination.current_page - 1) * itemsPerPage + idx + 1}
                  </td>
                  <td>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: q.question.substring(0, 100),
                      }}
                    />
                  </td>
                  <td>
                    <small>
                      A:{" "}
                      <span
                        dangerouslySetInnerHTML={{
                          __html: q.option_a?.substring(0, 50) || "",
                        }}
                      />
                    </small>
                    <br />
                    <small>
                      B:{" "}
                      <span
                        dangerouslySetInnerHTML={{
                          __html: q.option_b?.substring(0, 50) || "",
                        }}
                      />
                    </small>
                    <br />
                    <small>
                      C:{" "}
                      <span
                        dangerouslySetInnerHTML={{
                          __html: q.option_c?.substring(0, 50) || "",
                        }}
                      />
                    </small>
                    <br />
                    <small>
                      D:{" "}
                      <span
                        dangerouslySetInnerHTML={{
                          __html: q.option_d?.substring(0, 50) || "",
                        }}
                      />
                    </small>
                  </td>
                  <td>
                    <Badge bg="success">
                      {q.correct_option?.toUpperCase()}
                    </Badge>
                  </td>
                  <td>{q.marks}</td>
                  <td>
                    {q.has_negative_marking ? (
                      <Badge bg="warning" text="dark">
                        -{q.negative_marks}
                      </Badge>
                    ) : (
                      <Badge bg="light" text="dark">
                        None
                      </Badge>
                    )}
                  </td>
                  <td>
                    <Button
                      variant="outline-warning"
                      size="sm"
                      className="me-1"
                      onClick={() => openEditModal(q)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        setDeletingQuestion(q);
                        setShowDeleteModal(true);
                      }}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {renderPagination()}
        </>
      )}

      {/* Add/Edit Question Modal */}
      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingQuestion ? "Edit Question" : "Add New Question"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Question *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter the question (HTML supported like &lt;pre&gt;code&lt;/pre&gt;)"
                value={formData.question}
                onChange={(e) =>
                  setFormData({ ...formData, question: e.target.value })
                }
              />
              {formData.question && (
                <div className="mt-2 p-2 bg-light rounded small">
                  <strong>Preview:</strong>
                  <div className="mt-1" dangerouslySetInnerHTML={{ __html: formData.question }} />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Option A *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Option A (HTML supported)"
                value={formData.option_a}
                onChange={(e) =>
                  setFormData({ ...formData, option_a: e.target.value })
                }
              />
              {formData.option_a && (
                <div className="mt-1 p-2 bg-light rounded small">
                  <strong>Preview:</strong>
                  <div dangerouslySetInnerHTML={{ __html: formData.option_a }} />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Option B *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Option B (HTML supported)"
                value={formData.option_b}
                onChange={(e) =>
                  setFormData({ ...formData, option_b: e.target.value })
                }
              />
              {formData.option_b && (
                <div className="mt-1 p-2 bg-light rounded small">
                  <strong>Preview:</strong>
                  <div dangerouslySetInnerHTML={{ __html: formData.option_b }} />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Option C *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Option C (HTML supported)"
                value={formData.option_c}
                onChange={(e) =>
                  setFormData({ ...formData, option_c: e.target.value })
                }
              />
              {formData.option_c && (
                <div className="mt-1 p-2 bg-light rounded small">
                  <strong>Preview:</strong>
                  <div dangerouslySetInnerHTML={{ __html: formData.option_c }} />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Option D *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Option D (HTML supported)"
                value={formData.option_d}
                onChange={(e) =>
                  setFormData({ ...formData, option_d: e.target.value })
                }
              />
              {formData.option_d && (
                <div className="mt-1 p-2 bg-light rounded small">
                  <strong>Preview:</strong>
                  <div dangerouslySetInnerHTML={{ __html: formData.option_d }} />
                </div>
              )}
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Correct Answer *</Form.Label>
                  <Form.Select
                    value={formData.correct_option}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        correct_option: e.target.value,
                      })
                    }
                  >
                    <option value="a">A</option>
                    <option value="b">B</option>
                    <option value="c">C</option>
                    <option value="d">D</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Marks for Correct Answer</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={formData.marks}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        marks: parseInt(e.target.value) || 1,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Enable Negative Marking (deduct points for wrong answers)"
                checked={formData.has_negative_marking}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    has_negative_marking: e.target.checked,
                    negative_marks: e.target.checked
                      ? formData.negative_marks || 1
                      : 0,
                  });
                }}
              />
            </Form.Group>

            {formData.has_negative_marking && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Negative Marks (deduct for wrong answer)
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max={formData.marks}
                  value={formData.negative_marks}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      negative_marks: parseInt(e.target.value) || 0,
                    })
                  }
                />
                <Form.Text className="text-muted">
                  Points to deduct for each wrong answer (max {formData.marks})
                </Form.Text>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveQuestion}>
            {editingQuestion ? "Update" : "Add"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Delete Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deletingQuestion && (
            <p>Are you sure you want to delete this question?</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteQuestion}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import Questions Modal */}
      {showImportModal && (
        <QuestionImportModal
          show={showImportModal}
          onHide={() => setShowImportModal(false)}
          targetCategoryId={categoryId}
          onSuccess={handleImportSuccess}
        />
      )}
    </div>
  );
};

export default ManageQuestions;